/**
 * Remember where to send the user after the session completed modal closes.
 * Survives minimize / full toggles and a soft reload of the floating player.
 */

const storageKey = sessionId => `amok.session.returnTo.${sessionId}`

export const rememberSessionReturnTo = (
  sessionId,
  returnTo,
  returnLabel = "",
) => {
  if (!sessionId || !returnTo) return
  try {
    sessionStorage.setItem(
      storageKey(String(sessionId)),
      JSON.stringify({ path: String(returnTo), label: String(returnLabel || "") }),
    )
  } catch {
    // ignore quota / private mode
  }
}

export const readSessionReturnTo = sessionId => {
  if (!sessionId) return null
  try {
    const raw = sessionStorage.getItem(storageKey(String(sessionId)))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.path) return null
    return {
      path: String(parsed.path),
      label: String(parsed.label || ""),
    }
  } catch {
    return null
  }
}

export const clearSessionReturnTo = sessionId => {
  if (!sessionId) return
  try {
    sessionStorage.removeItem(storageKey(String(sessionId)))
  } catch {
    // ignore
  }
}

/**
 * Build `/projects/:id/:tab` or `/admin/projects/:id/:tab` from the current URL.
 */
export const projectTabReturnTo = (pathname = "", tab = "sessions", projectId) => {
  const path = String(pathname || "")
  const isAdmin = path.startsWith("/admin/")
  const prefix = isAdmin ? "/admin/projects" : "/projects"
  const id =
    projectId ||
    path.match(/\/projects\/([^/]+)/)?.[1] ||
    null
  if (!id || !tab) return null
  return `${prefix}/${id}/${tab}`
}

/**
 * Prefer the current listing path; if opened on a session detail URL, strip the id.
 */
export const listingReturnToFromPath = (pathname = "") => {
  const path = String(pathname || "").replace(/\/+$/, "") || ""
  if (!path) return null
  const stripped = path.replace(/\/sessions\/[^/]+$/, "/sessions")
  return stripped || null
}

export const resolveSessionCompletedRedirect = ({
  returnTo,
  returnLabel,
  sessionId,
  isCitySession,
  cityId,
  projectId,
  pathname,
} = {}) => {
  const stored = !returnTo && sessionId ? readSessionReturnTo(sessionId) : null
  const path = returnTo || stored?.path || null
  const label = returnLabel || stored?.label || ""

  if (path) {
    return {
      path,
      label: label || "Redirecting...",
    }
  }

  if (isCitySession && cityId) {
    return {
      path: `/city-hub/${cityId}/shadow-working-events`,
      label: "Returning to Shadow Working Events...",
    }
  }

  const projectSessions =
    projectTabReturnTo(pathname, "sessions", projectId) ||
    (projectId ? `/projects/${projectId}/sessions` : null)

  if (projectSessions) {
    return {
      path: projectSessions,
      label: "Redirecting to sessions...",
    }
  }

  return null
}
